import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { useByoCodeTest } from 'new/components/flows/types/byo/core/useByoCodeTest';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import type { ByoCodeValueV2, ByoFile } from '../types';
import { SamplePreview } from '../SamplePreview';
import { ByoFileBrowser } from '../InputPanel/ByoFileBrowser';
import { extractError, extractLogs, extractRows, extractSchema } from './probeOutput';

export interface TestPanelProps {
  code: ByoCodeValueV2;
  credentialId: number;
  folderPath?: string | null;
  sourceConfig: any;
  getProbeSampleCallback?: (credentialId: number, stage: string, payload: any) => void;
  filesOnly?: boolean;
  hideCopyPath?: boolean;
  /**
   * When set, blocks Run and shows this as the tooltip/status reason — same
   * mechanism as the panel's own credential/file/code checks below, for a
   * validity concern the panel itself has no visibility into (e.g. an
   * invalid runtime variable in a sibling panel). `null`/undefined leaves
   * Run's own internal checks as the only gate.
   */
  externalDisabledReason?: string | null;
  'data-testid'?: string;
}

export function TestPanel({
  code,
  credentialId,
  folderPath,
  sourceConfig,
  getProbeSampleCallback,
  filesOnly = false,
  hideCopyPath = false,
  externalDisabledReason = null,
  'data-testid': dataTestId = 'byo-test-panel',
}: TestPanelProps) {
  const logsEnabled = useFeatureFlag('UI_ENABLE_BYO_TEST_LOGS');
  const [cancelled, setCancelled] = useState(false);
  const [testFile, setTestFile] = useState<ByoFile | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTestFile(null);
  }, [folderPath, credentialId]);

  const { run, cancel, isRunning, postProcessorOutput, error } = useByoCodeTest(
    code,
    credentialId,
    false,
    getProbeSampleCallback,
  );

  const onTest = () => {
    if (!testFile) return;
    setCancelled(false);
    setActiveTab(0);
    run({ path: testFile.path, sourceConfig, fetchParserOutput: false });
  };

  const onCancelTest = () => {
    cancel();
    setCancelled(true);
  };

  // A predefined pick is also run as `code.code` — empty until picked and loaded.
  const hasCode = !!code?.code?.trim();
  const disabledReason = !credentialId
    ? 'Connect a source first'
    : !testFile
      ? 'Pick a file to test on'
      : !hasCode
        ? code?.mode === 'predefined'
          ? 'Pick a function to use'
          : 'Write code first'
        : (externalDisabledReason ?? null);

  const output = cancelled ? null : postProcessorOutput;
  const rows = useMemo(() => extractRows(output), [output]);
  const schema = useMemo(() => extractSchema(output), [output]);
  const logs = useMemo(() => extractLogs(output), [output]);
  // A Python exception in the user's code can still come back as an
  // HTTP-200 probe response (see extractError's own doc) — `error` above
  // only catches a non-200/202 stage status, so this is a SEPARATE check
  // against the parsed response itself. Only shown once the run has
  // actually finished with zero rows — a real result with output rows
  // should never be shadowed by a coincidental "Error:"-looking log line.
  const backendError = useMemo(
    () => (!isRunning && rows.length === 0 ? extractError(output) : null),
    [output, isRunning, rows.length],
  );
  // A failing process() comes back as a summary line, a blank line, then the
  // Python traceback. Show the summary as the message and the traceback as
  // code under it, keeping its indentation and ^^^ marker. NEX-22688.
  const [errorSummary, ...errorRest] = (error ?? backendError ?? '').split('\n\n');
  const errorDetail = errorRest.join('\n\n');
  const showError = Boolean(error || backendError) && !cancelled;
  const showRunning = isRunning && !cancelled;

  // Auto-scroll logs terminal when new lines arrive
  useEffect(() => {
    if (activeTab === 1) logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs, activeTab]);

  const recordsLabel = rows.length ? `Records · ${rows.length}` : 'Records';
  const logsLabel = logs.length ? `Logs · ${logs.length}` : 'Logs';

  return (
    <Box
      data-testid={dataTestId}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}
    >
      {/* File browser */}
      {credentialId ? (
        <Box
          sx={{
            height: 240,
            flexShrink: 0,
            border: '1px solid var(--color-greyscale-200, #e6e7eb)',
            borderRadius: '10px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            '& > *': { flex: 1, minHeight: 0 },
          }}
        >
          <ByoFileBrowser
            credentialId={credentialId}
            selectMode="file"
            initialFolder={folderPath ?? null}
            selectedPath={testFile?.path}
            onSelectFile={setTestFile}
            filesOnly={filesOnly}
            hideCopyPath={hideCopyPath}
            data-testid={`${dataTestId}-file-browser`}
          />
        </Box>
      ) : (
        <Alert severity="info">Connect a source to pick a test file.</Alert>
      )}

      {/* Run row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Tooltip title={disabledReason ?? ''} disableHoverListener={!disabledReason}>
          <span>
            <button
              type="button"
              onClick={onTest}
              disabled={!!disabledReason || showRunning}
              data-testid={`${dataTestId}-run`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background:
                  !!disabledReason || showRunning
                    ? 'var(--color-greyscale-200, #e5e7eb)'
                    : 'var(--color-primary-600, #2563eb)',
                color:
                  !!disabledReason || showRunning ? 'var(--color-greyscale-400, #9ca3af)' : '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 700,
                cursor: !!disabledReason || showRunning ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {showRunning ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />
              )}
              {showRunning ? 'Running…' : 'Run'}
            </button>
          </span>
        </Tooltip>

        {showRunning ? (
          <button
            type="button"
            onClick={onCancelTest}
            data-testid={`${dataTestId}-cancel`}
            style={{
              background: 'transparent',
              color: 'var(--color-greyscale-600, #4b5563)',
              border: '1px solid var(--color-greyscale-300, #d1d5db)',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Cancel
          </button>
        ) : null}

        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            fontSize: 12,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          data-testid={`${dataTestId}-status`}
        >
          {showRunning
            ? `Running on ${testFile?.name}…`
            : output
              ? `${rows.length} record${rows.length !== 1 ? 's' : ''} · ${testFile?.name}`
              : (disabledReason ?? `Ready · ${testFile?.name}`)}
        </Typography>
      </Box>

      {/* Results panel — tabs + content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--color-greyscale-200, #e6e7eb)',
          borderRadius: '10px',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            flexShrink: 0,
            minHeight: 36,
            borderBottom: '1px solid var(--color-greyscale-200, #e6e7eb)',
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          <Tab label={recordsLabel} data-testid={`${dataTestId}-tab-records`} />
          {logsEnabled ? <Tab label={logsLabel} data-testid={`${dataTestId}-tab-logs`} /> : null}
        </Tabs>

        {/* Records tab */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: output || showError ? 1.5 : 0,
            display: activeTab !== 0 ? 'none' : 'flex',
            flexDirection: 'column',
          }}
          data-testid={`${dataTestId}-output`}
        >
          {/* A failed run is this panel's result: show it here, not above the
              panel, where it squeezed Records down to just its tab strip. */}
          {showError ? (
            <Alert
              severity="error"
              sx={{ flexShrink: 0, '& .MuiAlert-message': { flex: 1, minWidth: 0 } }}
              data-testid={`${dataTestId}-error`}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {errorSummary}
              </Typography>
              {errorDetail ? (
                <Typography
                  component="pre"
                  data-testid={`${dataTestId}-error-detail`}
                  sx={{
                    // Reset the global (Bootstrap) pre box: the alert is the only container.
                    m: 0,
                    mt: 1,
                    p: 0,
                    border: 0,
                    borderRadius: 0,
                    bgcolor: 'transparent',
                    color: 'inherit',
                    fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
                    fontSize: 11.5,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {errorDetail}
                </Typography>
              ) : null}
            </Alert>
          ) : output && (rows.length || schema.length) ? (
            <SamplePreview schema={schema} rows={rows} />
          ) : output && !backendError && !showRunning ? (
            // The response came back (visible in the status line above) but
            // didn't match any recognized records/logs shape — e.g. a
            // processor that returns something other than row data. Showing
            // nothing here looked identical to "the run produced zero rows,"
            // silently discarding a real response. Fall back to the raw
            // payload so there's always visible confirmation of what came
            // back, matching the legacy DirectFlow test panel's raw dump.
            //
            // `!showRunning` guards against a distinct bug: `output` is a
            // Redux-backed value that already reflects the in-flight job's
            // raw 202 polling envelope (`{statusCode:202,...}`) the instant
            // the first poll response lands — well before the run actually
            // finishes. Without this guard, that envelope took this same
            // "unrecognized shape" branch and got shown to the user as if it
            // were their code's real output, while the Run button still said
            // "Running…". Keep showing the running state below until the
            // final (non-202) response is in.
            <Box>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: 12, mb: 1 }}
                data-testid={`${dataTestId}-raw-output-note`}
              >
                Response received, but it isn't shaped as row records — showing the raw output:
              </Typography>
              <Typography
                component="pre"
                data-testid={`${dataTestId}-raw-output`}
                sx={{
                  m: 0,
                  fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
                  fontSize: 11.5,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {JSON.stringify(output, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>
                {showRunning ? 'Running…' : 'Run a test to see output records.'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Logs tab — gated behind UI_ENABLE_BYO_TEST_LOGS until
            nexla_utils log output is actually wired through the probe
            response; see the flag's description in featureFlags.ts. */}
        {logsEnabled ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              background: '#1a1f29',
              p: 1.5,
              display: activeTab !== 1 ? 'none' : 'block',
            }}
            data-testid={`${dataTestId}-logs`}
          >
            {logs.length ? (
              <>
                {logs.map((line, idx) => (
                  <Typography
                    key={idx}
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
                      fontSize: 11.5,
                      lineHeight: 1.7,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color:
                        line.startsWith('ERROR') || line.startsWith('error')
                          ? '#f87171'
                          : line.startsWith('WARN') || line.startsWith('warn')
                            ? '#fbbf24'
                            : '#d1d5db',
                    }}
                  >
                    <Box component="span" sx={{ color: '#4b5563', mr: 1.5, userSelect: 'none' }}>
                      {String(idx + 1).padStart(3, ' ')}
                    </Box>
                    {line}
                  </Typography>
                ))}
                <div ref={logsEndRef} />
              </>
            ) : (
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: 12, color: '#4b5563' }}
              >
                {showRunning
                  ? 'Waiting for logs…'
                  : output
                    ? 'No logs emitted.'
                    : 'Run a test to see logs.'}
              </Typography>
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default TestPanel;
